/* eslint-disable @typescript-eslint/no-explicit-any */
import {Component, EventEmitter, Input, OnChanges, OnInit, ViewChild} from '@angular/core';
import {NgClass} from '@angular/common';
import {NlrHasPermissionsPipe} from '../../../pipe/nlr-has-permissions.pipe';
import {NlrPermissionsDisabledPipe} from '../../../pipe/nlr-permissions-disabled-pipe';
import {NlrFormFieldHasErrorPipe} from '../../../pipe/nlr-form-field-has-error.pipe';
import {ControlContainer, FormsModule, NgForm} from '@angular/forms';
import {NlrTypeheadDirective} from '../../../directive/nlr-typehead.directive';
import {NlrPatternDirective} from '../../../directive/nlr-pattern.directive';
import {BurdensAffectedTenureComponent} from '../../../../interests/burdens-affected-tenure/burdens-affected-tenure.component';
import {NlrPatternExcludeParserDirective} from '../../../directive/nlr-pattern-exclude-parser.directive';
import {ModalComponent} from '../modal-service.service';
import {LookupService} from '../../../service/lookup.service';
import {PlanModelService} from '../../../service/plan-model.service';
import {SpatialUtilService} from '../../../../design/services/spatial-util.service';
import {PATTERNS} from '../../../constants';
import {TraitsService} from '../../../service/traits.service';
import {TenureService} from '../../../../plan-information/tenure/tenure.service';
import {ValidationService} from '../../../service/validation.service';
import {Observable} from 'rxjs';
import {forkJoinDefaultIfEmpty} from '../../../observable.utils';
import {NlrFocusDirective} from '../../../directive/nlr-focus.directive';
import {NgSelectModule} from '@ng-select/ng-select';
import {ConfirmationModalService} from '../confirmation-modal/confirmation-modal.service';

@Component({
    selector: 'tenure-interests-notification-modal',
    imports: [
        NlrHasPermissionsPipe,
        NlrPermissionsDisabledPipe,
        NlrFormFieldHasErrorPipe,
        NgClass,
        FormsModule,
        NlrTypeheadDirective,
        NlrPatternDirective,
        BurdensAffectedTenureComponent,
        NlrPatternExcludeParserDirective,
        NlrFocusDirective,
        NgSelectModule
    ],
    templateUrl: './tenure-interest-notification-modal.component.html',
    styleUrls: ['../modal.scss', './tenure-interest-notification-modal.component.scss'],
    viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class TenureInterestsNotificationModalComponent implements ModalComponent, OnInit, OnChanges {
    readonly DOCUMENT_PATTERN = PATTERNS.DOCUMENT_PATTERN;
    readonly SURVEY_PATTERN = PATTERNS.SURVEY_PATTERN;
    readonly ALPHANUMERIC_PATTERN = PATTERNS.ALPHANUMERIC_PATTERN;
    readonly LOT_PATTERN = PATTERNS.LOT_PATTERN;
    @Input()
    tenureInterestNotification: any;
    @Input()
    overwritableSubjectVals: any;
    modalClosed: EventEmitter<undefined | string> = new EventEmitter();
    @ViewChild('tenInNoModalForm') form: NgForm;

    /**
     * Manually track dirty changes on burdenAffectedLots as Angular can't
     */
    burdenAffectedLotsDirty = {};

    title = '';
    interestsUiConfigLookup: any;
    notificationTreatmentActionLookups: any;
    interestSubjectTypeLookups: any;
    formerTenureNotificationLookupForNotificationCodeOverride: any;
    formerTenureNotificationLookupForNotificationCode: any;
    statutoryReferenceOptions: any;
    fieldErrorMessages = new Map();
    showSubjectForAction = false;
    subjectsInvalid = false;
    currentDocumentLabel = '';

    constructor(
        private planModelService: PlanModelService,
        private lookupService: LookupService,
        private tenureService: TenureService,
        private validationService: ValidationService,
        private spatialUtilService: SpatialUtilService,
        private traitsService: TraitsService,
        private confirmationModalService: ConfirmationModalService
    ) {}

    ngOnInit(): void {
        this.interestsUiConfigLookup = this.lookupService
            .getLookups('interestsUiConfig')
            .find(iucl => iucl.parentTenureAssociationType === this.planModelService.getModel().planPurpose.parentTenureAssociationType);
        this.title = this.interestsUiConfigLookup ? this.interestsUiConfigLookup.broughtForwardNotificationModalName : '';

        this.notificationTreatmentActionLookups = this.lookupService.getLookups('notificationTreatmentAction');
        this.interestSubjectTypeLookups = this.lookupService.getLookups('interestSubjectType');
        const formerTenureNotificationLookups = this.lookupService.getLookups('formerTenureNotification');
        this.formerTenureNotificationLookupForNotificationCodeOverride = this.tenureInterestNotification.notificationCodeOverride
            ? formerTenureNotificationLookups.find(l => l.notificationCode === this.tenureInterestNotification.notificationCodeOverride)
            : null;

        this.formerTenureNotificationLookupForNotificationCode = formerTenureNotificationLookups.find(
            l => l.notificationCode === this.tenureInterestNotification.notificationCode
        );

        const overrideNotificationCodes = this.formerTenureNotificationLookupForNotificationCode.overrideNotificationCodes;
        this.statutoryReferenceOptions =
            overrideNotificationCodes.length === 0
                ? []
                : formerTenureNotificationLookups
                      .filter(l => overrideNotificationCodes.includes(l.notificationCode))
                      .sort((l1, l2) => l1.notificationCode.localeCompare(l2.notificationCode));
        this.showSubjects(this.tenureInterestNotification.treatmentAction?.id);
    }

    ngOnChanges() {
        this.showSubjects(this.tenureInterestNotification.treatmentAction?.id);
    }

    readOnly = () => {
        return this.planModelService.readonly;
    };

    dismissModal = form => {
        if (form.pristine) {
            this.modalClosed.emit('Closed');
            return;
        }
        this.confirmationModalService
            .showModal({
                title: 'Warning',
                confirmationText: `You are about to lose all changes. Are you sure you wish to continue?`
            })
            .subscribe(action => {
                if (action === 'yes') {
                    this.modalClosed.emit('Closed');
                }
            });
    };

    getLabel = labelName => {
        return this.interestsUiConfigLookup ? this.interestsUiConfigLookup[labelName] : '';
    };

    isOriginDocumentRequired = () => {
        return this.getEffectiveFormerTenureNotificationLookup().mandatoryOriginDocumentNumber;
    };

    getEffectiveFormerTenureNotificationLookup() {
        return this.formerTenureNotificationLookupForNotificationCodeOverride || this.formerTenureNotificationLookupForNotificationCode;
    }

    originDocumentChanged = (form: any) => {
        const currentErrors = form.controls['originDocument'].errors;
        if (currentErrors) {
            delete currentErrors.validIdentifier;
        }
        this.fieldErrorMessages.delete('originDocument');
    };

    getFieldErrorMessage = fieldName => {
        return this.fieldErrorMessages.get(fieldName);
    };

    isOriginPlanRequired = () => {
        return this.getEffectiveFormerTenureNotificationLookup().planOriginRequired;
    };

    originPlanChanged = (form: any) => {
        const currentErrors = form.controls['originPlan'].errors;
        if (currentErrors) {
            delete currentErrors.validIdentifier;
        }
        form.controls['originPlan'].setErrors(currentErrors);
        this.fieldErrorMessages.delete('originPlan');
    };

    filteredNotificationTreatmentActionLookups = () => {
        return this.getEffectiveFormerTenureNotificationLookup()
            .notificationTreatmentActions.map(treatmentAction => this.notificationTreatmentActionLookups.find(v => v.id === treatmentAction))
            .filter(ntal => {
                if (!ntal || !ntal.active) {
                    return false;
                }

                const {parentTenureAssociationType} = this.planModelService.getModel().planPurpose;

                if (ntal.parentTenureAssociationType !== parentTenureAssociationType) {
                    return false;
                }

                if (!ntal.planTraitCriteria) {
                    return true;
                }

                return this.traitsService.criteriaMatches(ntal.planTraitCriteria, false);
            });
    };

    treatmentActionUpdated = treatmentActionIdSelected => {
        this.showSubjects(treatmentActionIdSelected);
        this.tenureInterestNotification.treatmentAction = this.filteredNotificationTreatmentActionLookups().find(
            ta => ta.id === treatmentActionIdSelected
        );
    };

    showSubjects = treatmentActionSelected => {
        const newSubjects = [];

        if (this.tenureInterestNotification.action !== 'SURRENDERED') {
            this.tenureInterestNotification.treatmentAction = this.notificationTreatmentActionLookups.find(v => v.id === treatmentActionSelected);

            if (
                this.tenureInterestNotification.treatmentAction &&
                this.tenureInterestNotification.treatmentAction.interestSubjectTypes &&
                this.tenureInterestNotification.treatmentAction.interestSubjectTypes.length
            ) {
                this.showSubjectForAction = true;
                this.currentDocumentLabel = this.tenureInterestNotification.treatmentAction.docNumberFieldLabel || '';

                this.tenureInterestNotification.treatmentAction.interestSubjectTypes.forEach(interestSubjectType => {
                    const istLookup = this.interestSubjectTypeLookups.find(v => v.id === interestSubjectType);
                    const subject = this.tenureInterestNotification.subjects.find(s => s.subjectType && s.subjectType.id === interestSubjectType);

                    this.tenureInterestNotification.subjects.find(subj => subj.id);

                    if (subject) {
                        newSubjects.push(subject);
                    } else {
                        newSubjects.push({subjectType: istLookup, affectedLots: [{startLot: '', endLot: ''}]});
                    }
                });
            } else {
                this.resetSurrenderedFields();
            }
        } else {
            if (this.tenureInterestNotification.treatmentAction) {
                delete this.tenureInterestNotification.treatmentAction;
            }
            this.resetSurrenderedFields();
        }

        this.tenureInterestNotification.subjects.splice(0, this.tenureInterestNotification.subjects.length, ...newSubjects);

        this.configAffectedLotRanges();
    };

    resetSurrenderedFields = () => {
        this.showSubjectForAction = false;
        delete this.tenureInterestNotification.treatmentDocument;
        if (this.form && this.form.controls['treatmentDocument']) {
            const currentErrors = this.form.controls['treatmentDocument'].errors;
            if (currentErrors) {
                delete currentErrors['validIdentifier'];
            }
        }
        this.fieldErrorMessages.delete('treatmentDocument');
        this.currentDocumentLabel = '';
    };

    configAffectedLotRanges = () => {
        this.tenureInterestNotification.subjects.forEach(subject => {
            subject.affectedLots.splice(
                0,
                subject.affectedLots.length,
                ...this.tenureService
                    .reduceRanges(subject.affectedLots.map(v => ({from: v.startLot, to: v.endLot})))
                    .map(v => ({startLot: v.from, endLot: v.to}))
            );
        });
    };

    actionPdfCreationMethodInvalidForPlan = () => {
        if (
            this.tenureInterestNotification &&
            this.tenureInterestNotification.treatmentAction &&
            this.tenureInterestNotification.treatmentAction.supportedPdfCreationMethods
        ) {
            const planPDFCreationMethod = this.planModelService.getModel().pdfCreationMethod;
            return !this.tenureInterestNotification.treatmentAction.supportedPdfCreationMethods.some(spcm => planPDFCreationMethod === spcm);
        }
        return undefined;
    };

    unsupportedMessage = () => {
        return this.tenureInterestNotification.treatmentAction.unsupportedMessage;
    };

    isDuplicateSubject = (interestSubject, subjectIndex) => {
        if (interestSubject.subject) {
            const upperCaseSubject = interestSubject.subject.toUpperCase();

            this.subjectsInvalid =
                (this.planModelService
                    .getModel()
                    .formerTenures.some(ft =>
                        ft.formerTenureNotifications.some(ftn =>
                            ftn.subjects.some(
                                subject => subject.subject && subject.subject.toUpperCase() === upperCaseSubject && subject.id !== interestSubject.id
                            )
                        )
                    ) ||
                    this.tenureInterestNotification.subjects.some(
                        (subject, index) =>
                            subject.subject &&
                            subject.subject.toUpperCase() === upperCaseSubject &&
                            index !== subjectIndex &&
                            subject.subject.length > 0
                    )) &&
                (!this.overwritableSubjectVals.some(subVal => subVal && subVal.toUpperCase() === upperCaseSubject) ||
                    this.tenureInterestNotification.subjects.filter(subj => subj.subject && subj.subject.toUpperCase() === upperCaseSubject).length >
                        1);
        } else {
            this.subjectsInvalid = false;
        }

        return this.subjectsInvalid;
    };

    showAffectedLots = index => {
        if (this.interestsUiConfigLookup && this.interestsUiConfigLookup.broughtForwardNotificationsNewTenureFieldLabel) {
            //reconstruct empty affected lots if none exist
            if (
                !this.tenureInterestNotification.subjects[index].affectedLots ||
                this.tenureInterestNotification.subjects[index].affectedLots.length == 0
            ) {
                this.tenureInterestNotification.subjects[index].affectedLots = [];
                this.tenureInterestNotification.subjects[index].affectedLots.push({startLot: '', endLot: ''});
            }

            return true;
        } else {
            return false;
        }
    };

    onChangeAffectedLot = (interestSubject, idx, newAffectedLot) => {
        this.burdenAffectedLotsDirty[idx] = true;
        if (newAffectedLot) {
            const lots = newAffectedLot
                .split('-')
                .map(v => parseInt(v))
                .filter(v => !isNaN(v));
            if (lots.length === 1) {
                Object.assign(interestSubject.affectedLots[idx], {startLot: lots[0], endLot: lots[0]});
                return;
            }
            if (lots.length === 2) {
                const [startLot, endLot] = lots;
                Object.assign(interestSubject.affectedLots[idx], {startLot: startLot, endLot: endLot});
                return;
            }
            Object.assign(interestSubject.affectedLots[idx], newAffectedLot);
        } else {
            interestSubject.affectedLots[idx] = {startLot: '', endLot: ''};
        }
    };

    getBurdenAffectedLotForDisplay = burdenAffectedLot => {
        if (burdenAffectedLot) {
            if (burdenAffectedLot.startLot && burdenAffectedLot.endLot && burdenAffectedLot.startLot !== burdenAffectedLot.endLot) {
                return `${burdenAffectedLot.startLot}-${burdenAffectedLot.endLot}`;
            }
            if (burdenAffectedLot.startLot) {
                return `${burdenAffectedLot.startLot}`;
            }
        }
        return '';
    };

    addLotRecord = interestSubject => {
        interestSubject.affectedLots.push({});
    };

    removeLotRecord = (interestSubject, burdenAffectedLot) => {
        interestSubject.affectedLots.splice(
            0,
            interestSubject.affectedLots.length,
            ...interestSubject.affectedLots.filter(affectedLot => affectedLot !== burdenAffectedLot)
        );
    };

    treatmentDocumentChanged = (form: any) => {
        const currentErrors = form.controls['treatmentDocument'].errors;
        if (currentErrors) {
            delete currentErrors.validIdentifier;
        }
        this.fieldErrorMessages.delete('treatmentDocument');
    };

    save = () => {
        if (this.form.invalid) {
            return;
        }

        const observables: Observable<unknown>[] = [];

        if (this.tenureInterestNotification.originDocument) {
            const docIdObs = this.validationService.validateDocumentIdentifier(this.tenureInterestNotification.originDocument);
            docIdObs.subscribe(valid => {
                if (valid) {
                    this.form.controls['originDocument'].setErrors(null);
                } else {
                    this.form.controls['originDocument'].setErrors({
                        validIdentifier: !valid
                    });
                }

                if (valid) {
                    this.fieldErrorMessages.delete('originDocument');
                } else {
                    this.fieldErrorMessages.set('originDocument', 'Document number is not a valid number');
                }
            });
            observables.push(docIdObs);
        }

        if (this.tenureInterestNotification.originPlan) {
            const surveyIdObs = this.validationService.validateSurveyIdentifier(this.tenureInterestNotification.originPlan);
            surveyIdObs.subscribe(valid => {
                if (valid) {
                    this.form.controls['originPlan'].setErrors(null);
                } else {
                    this.form.controls['originPlan'].setErrors({
                        validIdentifier: !valid
                    });
                }

                if (valid) {
                    this.fieldErrorMessages.delete('originPlan');
                } else {
                    this.fieldErrorMessages.set('originPlan', 'Plan number is not a valid number');
                }
            });
            observables.push(surveyIdObs);
        }

        if (this.tenureInterestNotification.treatmentDocument) {
            const docIdObs = this.validationService.validateDocumentIdentifier(this.tenureInterestNotification.treatmentDocument);
            docIdObs.subscribe(valid => {
                if (valid) {
                    this.form.controls['treatmentDocument'].setErrors(null);
                } else {
                    this.form.controls['treatmentDocument'].setErrors({
                        validIdentifier: !valid
                    });
                }

                if (valid) {
                    this.fieldErrorMessages.delete('treatmentDocument');
                } else {
                    this.fieldErrorMessages.set('treatmentDocument', 'Document number is not a valid number');
                }
            });
            observables.push(docIdObs);
        }

        forkJoinDefaultIfEmpty(observables).subscribe(() => {
            if (this.fieldErrorMessages.size === 0) {
                this.saveAndUpdate();
            }
        });
    };

    saveAndUpdate = () => {
        if (this.tenureInterestNotification.action === 'SURRENDERED') {
            this.tenureInterestNotification.subject = '';
        }

        this.sortSubjectLots();
        this.reduceAffectedLotRanges();

        this.spatialUtilService.save().subscribe(() => {
            this.modalClosed.emit(this.tenureInterestNotification);
        });
    };

    /**
     * Sort subject lots so that they are in numerical order. I.e. lower numbered lots are at .startLots, and higher numbered lots are at .endLots.
     * Given startLot: 12 and endLot: 10, this will sort so that the result is startLot: 10 and endLot: 12.
     * This is required for tenureService.reduceRanges to work correctly
     */
    sortSubjectLots = () => {
        for (const subject of this.tenureInterestNotification.subjects) {
            for (const affectedLots of subject.affectedLots) {
                if (affectedLots.startLot !== affectedLots.endLot) {
                    const startLot = affectedLots.startLot;
                    const endLot = affectedLots.endLot;
                    affectedLots.startLot = startLot > endLot ? endLot : startLot;
                    affectedLots.endLot = startLot > endLot ? startLot : endLot;
                }
            }
        }
    };

    reduceAffectedLotRanges = () => {
        this.tenureInterestNotification.subjects.forEach(subject => {
            subject.affectedLots.splice(
                0,
                subject.affectedLots.length,
                ...this.tenureService
                    .reduceRanges(subject.affectedLots.map(v => ({from: v.startLot, to: v.endLot})))
                    .map(v => ({startLot: v.from, endLot: v.to}))
            );

            subject.affectedLots = subject.affectedLots.filter(affectedLot => affectedLot.startLot);
        });
    };

    burdenActionHasError = () => {
        if (!this.form) {
            return false;
        }
        let error = false;
        Object.keys(this.form.controls).forEach(key => {
            if (key.startsWith('burdenAction')) {
                if (this.form.controls[key].invalid && this.form.controls[key].dirty) {
                    error = true;
                }
            }
        });

        return error;
    };

    burdenAffectedLotHasError = ctrlIdx => {
        if (!this.form || !this.form.controls[`burdenAffectedLot${ctrlIdx}`]) {
            return false;
        }

        return this.form.controls[`burdenAffectedLot${ctrlIdx}`].invalid && this.burdenAffectedLotsDirty[ctrlIdx];
    };
}
